import { PoolCardProps } from '@synergy/components-next';
import { ReactElement, createContext, useContext } from 'react';

import { ArticleCardLandscape } from './components-landscape/article-card-landscape';
import { ArticleCardPortrait } from './components-portrait/article-card-portrait';
import { ArticleListItem } from '../../models/article-list-item';
import { ROUTES } from '../../utils/routes';
import { addArticleToCart } from '../../store/shopping-cart-article-actions';
import { addToWishlist } from '../../store/whish-list-actions';
import { openInquiryModal } from '../../store/single-inquiry-actions';
import { reportSearchInteraction } from '../../store/search-interaction-actions';
import { useAppDispatch } from '../../../store/store';

export type ArticleCardOrientation = 'portrait' | 'landscape';

export interface ArticleCardProps extends PoolCardProps {
    article: ArticleListItem;
    orientation?: ArticleCardOrientation;
    /**
     * Set where the card stands in a search result. Opening or ordering the article
     * is then reported back against that search — the position is the point: the
     * same article opened from rank one means something else than from rank forty.
     */
    searchTracking?: { searchEventId?: number | null; position: number };
}

interface ArticleCardContextProps {
    article: ArticleListItem;
    href?: string;
    onOpen?: () => void;
    onAddToShoppingCart?: () => void;
    onToggleFavorite?: () => void;
    onInquiryClick?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ArticleCardContext = createContext<ArticleCardContextProps>({ article: undefined! });
export const useArticleCardContext = (): ArticleCardContextProps => useContext(ArticleCardContext);

/**
 * ArticleCard<br/>
 * <a href="https://www.figma.com/file/UXG8mmJQdrAX2oDRp0FTBl/Pool-Synergy-Shop?node-id=3699%3A403915" target="_blank">Open in Figma</a>
 */
export const ArticleCard = (props: ArticleCardProps): ReactElement => {
    const { article, orientation, searchTracking, ...cardProps } = props;
    const dispatch = useAppDispatch();

    const handleAddToShoppingCart = (): void => {
        reportSearchInteraction(searchTracking?.searchEventId, 'cart', article.id, searchTracking?.position);
        dispatch(addArticleToCart(article.id, article.minimumOrderQuantity));
    };

    const handleOpen = (): void => {
        reportSearchInteraction(searchTracking?.searchEventId, 'click', article.id, searchTracking?.position);
    };

    const handleAddToWishList = (): void => {
        dispatch(addToWishlist('default', article.id, article.minimumOrderQuantity));
    };

    const handleSingleInquiry = (): void => {
        dispatch(
            openInquiryModal({
                items: [{ id: article.id, articleName: article.name, quantity: article.minimumOrderQuantity }],
            })
        );
    };

    return (
        <ArticleCardContext.Provider
            value={{
                article,
                onOpen: handleOpen,
                href: ROUTES.article.detail.replace(':id', article.id.toString()),
                onAddToShoppingCart: handleAddToShoppingCart,
                onToggleFavorite: handleAddToWishList,
                onInquiryClick: handleSingleInquiry,
            }}
        >
            {orientation === 'landscape' ? (
                <ArticleCardLandscape {...cardProps} />
            ) : (
                <ArticleCardPortrait {...cardProps} />
            )}
        </ArticleCardContext.Provider>
    );
};
